import { useState } from 'react';
import { Anchor } from 'lucide-react';
import { supabase } from '../lib/supabase';

// Staff sign-in screen. Authenticates against Supabase Auth (email/password).
// On success, App's onAuthStateChange listener swaps to the app — this screen
// just signs in; it does not own session state. No credentials are hardcoded.
const inputStyle = {
  width: '100%',
  background: 'var(--color-sunken)',
  border: '1.5px solid var(--color-border-strong)',
  borderRadius: 9,
  padding: '12px 14px',
  fontSize: '1rem',
  color: '#0A1628',
  outline: 'none',
  transition: 'border-color 0.2s, box-shadow 0.2s',
  boxSizing: 'border-box',
};
const focusOn = (e) => { e.target.style.borderColor = '#C9A84C'; e.target.style.boxShadow = '0 0 0 3px #C9A84C22'; };
const focusOff = (e) => { e.target.style.borderColor = 'var(--color-border-strong)'; e.target.style.boxShadow = 'none'; };
const labelStyle = {
  display: 'block',
  fontSize: '0.72rem',
  fontWeight: 700,
  color: 'var(--color-ink-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: 7,
};

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (signInError) {
        setError(
          /invalid login credentials/i.test(signInError.message || '')
            ? 'Incorrect email or password. Please try again.'
            : (signInError.message || 'Could not sign in. Please try again.')
        );
        setLoading(false);
        return;
      }
      // Success: leave the button in its submitting state — App's auth listener
      // unmounts this screen, so resetting loading would only flash the idle state.
    } catch (err) {
      setError('Could not reach the server. Check your connection and try again.');
      setLoading(false);
    }
  };

  const canSubmit = !loading && email && password;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--color-canvas)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px 16px',
    }}>
      <div style={{ width: '100%', maxWidth: 380 }}>
        {/* Logo + branding */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{
            width: 64,
            height: 64,
            background: '#C9A84C',
            borderRadius: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            boxShadow: '0 4px 20px #C9A84C44',
          }}>
            <Anchor size={34} strokeWidth={1.75} aria-hidden="true" />
          </div>
          <h1 className="app-wordmark" style={{ fontSize: '1.7rem', margin: '0 0 6px', whiteSpace: 'normal' }}>
            Alfresco Grand
          </h1>
          <p style={{
            fontSize: '0.82rem',
            color: 'var(--color-ink-muted)',
            letterSpacing: '0.3px',
          }}>
            Booking Management
          </p>
        </div>

        {/* Sign-in card */}
        <div style={{
          background: '#ffffff',
          border: '1px solid var(--color-border)',
          borderRadius: 16,
          padding: '32px 28px',
          boxShadow: '0 4px 24px rgba(10,22,40,0.08)',
        }}>
          <p style={{
            fontSize: '0.72rem',
            fontWeight: 700,
            color: 'var(--color-ink-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.8px',
            marginBottom: 20,
          }}>
            Staff Sign In
          </p>

          {error && (
            <div style={{
              background: '#fef2f2',
              border: '1px solid #fca5a5',
              borderRadius: 8,
              padding: '10px 14px',
              fontSize: '0.84rem',
              color: '#991b1b',
              marginBottom: 16,
              fontWeight: 500,
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 18 }}>
              <label htmlFor="login-email" style={labelStyle}>Email</label>
              <input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@alfrescogrand.com"
                autoFocus
                autoComplete="username"
                style={inputStyle}
                onFocus={focusOn}
                onBlur={focusOff}
              />
            </div>

            <div style={{ marginBottom: 22 }}>
              <label htmlFor="login-password" style={labelStyle}>Password</label>
              <input
                id="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
                style={inputStyle}
                onFocus={focusOn}
                onBlur={focusOff}
              />
            </div>

            <button
              type="submit"
              disabled={!canSubmit}
              style={{
                width: '100%',
                background: canSubmit ? '#C9A84C' : '#e8d89a',
                color: '#0A1628',
                border: 'none',
                borderRadius: 9,
                padding: '13px',
                fontSize: '0.92rem',
                fontWeight: 700,
                cursor: canSubmit ? 'pointer' : 'not-allowed',
                transition: 'all 0.18s',
                letterSpacing: '0.2px',
                boxShadow: canSubmit ? '0 2px 10px #C9A84C44' : 'none',
              }}
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        </div>

        <p style={{
          textAlign: 'center',
          marginTop: 24,
          fontSize: '0.75rem',
          color: 'var(--color-ink-faint)',
        }}>
          Guwahati, Assam
        </p>
      </div>
    </div>
  );
}
